// Network status indicator component
import React, { useState, useEffect } from 'react';
import { NetworkStatus, subscribeToNetworkStatus, networkManager } from '../../utils/networkManager';
import './NetworkStatusIndicator.css';

interface NetworkStatusIndicatorProps {
  showWhenOnline?: boolean;
  position?: 'top' | 'bottom';
  className?: string;
}

export const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({
  showWhenOnline = false,
  position = 'top',
  className = '',
}) => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({ isOnline: navigator.onLine });
  const [isVisible, setIsVisible] = useState(false);
  const [networkQuality, setNetworkQuality] = useState<string>('good');

  useEffect(() => {
    // Subscribe to network status changes
    const unsubscribe = subscribeToNetworkStatus((status) => {
      setNetworkStatus(status);
      setNetworkQuality(networkManager.getNetworkQuality());
      
      // Show indicator when offline or when explicitly requested for online status
      setIsVisible(!status.isOnline || showWhenOnline);
    });

    // Initial status check
    const initialStatus = networkManager.getNetworkStatus();
    setNetworkStatus(initialStatus);
    setNetworkQuality(networkManager.getNetworkQuality());
    setIsVisible(!initialStatus.isOnline || showWhenOnline);

    return unsubscribe;
  }, [showWhenOnline]);

  // Auto-hide online indicator after a few seconds
  useEffect(() => {
    if (networkStatus.isOnline && showWhenOnline && isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [networkStatus.isOnline, showWhenOnline, isVisible]);

  if (!isVisible) {
    return null;
  }

  const getStatusIcon = () => {
    if (!networkStatus.isOnline) {
      return '📶❌';
    }
    
    switch (networkQuality) {
      case 'excellent':
        return '📶';
      case 'good':
        return '📶';
      case 'fair':
        return '📶⚠️';
      case 'poor':
        return '📶🐌';
      default:
        return '📶';
    }
  };

  const getStatusMessage = () => {
    if (!networkStatus.isOnline) {
      return 'You\'re offline. Some features may not work.';
    }
    
    if (!showWhenOnline) {
      return null;
    }
    
    switch (networkQuality) {
      case 'excellent':
        return 'Excellent connection';
      case 'good':
        return 'Good connection';
      case 'fair':
        return 'Fair connection - some features may be slower';
      case 'poor':
        return 'Poor connection - please be patient';
      default:
        return 'Connected';
    }
  };

  const getStatusClass = () => {
    if (!networkStatus.isOnline) {
      return 'offline';
    }
    return networkQuality;
  };

  const containerClasses = [
    'network-status-indicator',
    `network-status-indicator--${position}`,
    `network-status-indicator--${getStatusClass()}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-label={`Network status: ${networkStatus.isOnline ? 'online' : 'offline'}`}
    >
      <div className="network-status-indicator__content">
        <span className="network-status-indicator__icon" aria-hidden="true">
          {getStatusIcon()}
        </span>
        
        <span className="network-status-indicator__message">
          {getStatusMessage()}
        </span>
        
        {!networkStatus.isOnline && (
          <button
            type="button"
            className="network-status-indicator__retry"
            onClick={() => window.location.reload()}
            aria-label="Retry connection"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};